
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { MobileNavigation } from "@/components/MobileNavigation";
import { SocialMetaTags } from "@/components/SocialMetaTags";
import { WebNovelContent } from "@/components/webnovel/WebNovelContent";
import { WebNovelSidebar } from "@/components/webnovel/WebNovelSidebar";
import { WebNovelsErrorState } from "@/components/webnovel/WebNovelsErrorState";
import { fetchWebNovel } from "@/components/webnovel/api/fetchWebNovel";
import { fetchChapter } from "@/components/webnovel/api/fetchChapter";
import { WebNovel, Chapter } from "@/components/webnovel/types";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

export default function WebNovelReader() {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [user, setUser] = useState<any>(null);
  
  // Fetch current user - optimized with memoization
  useEffect(() => {
    const fetchUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data.user);
    };
    
    fetchUser();
  }, []);
  
  // Add preconnect for Supabase
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://kcsydqftkfhmyewxtvgd.supabase.co';
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  
  // Fetch webnovel data with proper error handling
  const webnovelQuery = useQuery({
    queryKey: ["webnovel", id],
    queryFn: async () => {
      if (!id) throw new Error("No webnovel ID provided");
      return fetchWebNovel(id);
    },
    enabled: !!id,
    retry: 2,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
  
  // Fetch chapter data with proper error handling
  const chapterQuery = useQuery({
    queryKey: ["chapter", id, chapterId],
    queryFn: async () => {
      if (!id) throw new Error("No webnovel ID provided");
      return fetchChapter(id, Number(chapterId) || 1);
    },
    enabled: !!id && (!!chapterId || webnovelQuery.isSuccess),
    retry: 2,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
  
  // Calculate total pages based on chapter content
  useEffect(() => {
    if (chapterQuery.isSuccess && chapterQuery.data) {
      // Split chapter content into pages (each 1500 characters is roughly a page)
      const contentLength = chapterQuery.data.content?.length || 0;
      const calculatedPages = Math.max(1, Math.ceil(contentLength / 1500));
      setTotalPages(calculatedPages);
      
      // Reset to first page when changing chapters
      setCurrentPage(0);
    }
  }, [chapterQuery.data, chapterQuery.isSuccess]);
  
  // Handle page flipping
  const flipPage = useCallback((direction: 'next' | 'prev') => {
    setIsFlipping(true);
    
    setTimeout(() => {
      if (direction === 'next' && currentPage < totalPages - 1) {
        setCurrentPage(prev => prev + 1);
      } else if (direction === 'prev' && currentPage > 0) {
        setCurrentPage(prev => prev - 1);
      }
      
      setIsFlipping(false);
    }, 300);
  }, [currentPage, totalPages]);
  
  // Split content into pages and get current page content - memoized to prevent re-calculation
  const getCurrentPageContent = useCallback(() => {
    if (!chapterQuery.data?.content) return [""];
    
    // Split by paragraphs
    const paragraphs = chapterQuery.data.content.split("\n").filter(p => p.trim());
    
    // Group paragraphs into pages (simple approach)
    const pages: string[][] = [];
    let currentPageContent: string[] = [];
    let currentPageLength = 0;
    
    paragraphs.forEach(paragraph => {
      if (currentPageLength + paragraph.length > 1500) {
        pages.push([...currentPageContent]);
        currentPageContent = [paragraph];
        currentPageLength = paragraph.length;
      } else {
        currentPageContent.push(paragraph);
        currentPageLength += paragraph.length;
      }
    });
    
    // Add the last page if there's content
    if (currentPageContent.length > 0) {
      pages.push([...currentPageContent]);
    }
    
    // If there are no pages, return an empty array
    if (pages.length === 0) {
      return [""];
    }
    
    // Return the current page content (or first page if current page is out of bounds)
    return currentPage < pages.length ? pages[currentPage] : pages[0];
  }, [chapterQuery.data?.content, currentPage]);
  
  // Memoize the page content to prevent unnecessary calculations
  const currentPageContent = useMemo(() => getCurrentPageContent(), [getCurrentPageContent]);
  
  // Loading state
  if (webnovelQuery.isLoading || chapterQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Skeleton className="h-12 w-3/4 mb-4" />
              <Skeleton className="h-8 w-1/2 mb-8" />
              <div className="space-y-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </div>
            <div className="lg:col-span-1">
              <Skeleton className="h-[500px] w-full rounded-lg" />
            </div>
          </div>
        </div>
        <MobileNavigation />
      </div>
    );
  }
  
  // Error state
  if (webnovelQuery.error || chapterQuery.error) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <WebNovelsErrorState 
          error={webnovelQuery.error as Error || chapterQuery.error as Error} 
        />
        <MobileNavigation />
      </div>
    );
  }
  
  // Empty state - no webnovel or chapter found
  if (!webnovelQuery.data || !chapterQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 pt-20 pb-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Webnovel ou capítulo não encontrado</h2>
          <p className="text-muted-foreground mb-6">
            Não foi possível encontrar a webnovel ou capítulo solicitado.
          </p>
        </div>
        <MobileNavigation />
      </div>
    );
  }
  
  const webnovel = webnovelQuery.data;
  const chapter = chapterQuery.data;
  
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Cache control */}
        <meta http-equiv="Cache-Control" content="max-age=3600" />
        {/* Preload critical image */}
        {webnovel?.cover_url && (
          <link rel="preload" as="image" href={webnovel.cover_url} />
        )}
      </Helmet>
      
      {webnovel && chapter && (
        <SocialMetaTags
          title={`${chapter.title} - ${webnovel.title}`}
          description={webnovel.description || `Leia o capítulo ${chapter.chapter_number} de ${webnovel.title}`}
          imageUrl={webnovel.cover_url}
          url={window.location.href}
          type="article"
          contentType="webnovel"
          contentId={id}
        />
      )}
      
      <Navigation />
      <div className="container mx-auto px-4 pt-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* WebNovel Content */}
          <WebNovelContent
            novel={webnovel}
            chapter={chapter}
            currentPage={currentPage}
            totalPages={totalPages}
            isFlipping={isFlipping}
            flipPage={flipPage}
            getCurrentPageContent={() => currentPageContent}
          />
          
          {/* Sidebar with chapter navigation, novel info, etc. */}
          <WebNovelSidebar
            novel={webnovel}
            chapter={chapter}
            user={user}
            navigate={navigate}
          />
        </div>
      </div>
      <MobileNavigation />
    </div>
  );
}
